import Button from '@decisiv/ui-components/lib/components/Button';
import type {
  ButtonProps,
  IconComponent,
  IconPosition,
  Kind,
} from '@decisiv/ui-components/lib/components/Button/types';
import Menu from '@decisiv/ui-components/lib/components/Menu';
import Popover from '@decisiv/ui-components/lib/components/Popover';

import useMenuPopover from 'pages/Management/hooks/useMenuPopover';

export interface MenuButtonItem {
  id: string;
  text: string;
  disabled?: boolean;
  onSelect: () => void;
}

interface MenuButtonBaseProps {
  items: MenuButtonItem[];
  icon?: IconComponent;
  iconPosition?: IconPosition;
  kind?: Kind;
  disabled?: boolean;
  /**
   * z-index of the Popover holding the menu. Over a Management table, use
   * `TABLE_OVERLAY_MENU_Z_INDEX`.
   */
  zIndex?: number;
}

/**
 * The trigger needs an accessible name: visible `text`, an `aria-label`, or
 * both. An icon-only trigger must pass `aria-label`.
 */
type MenuButtonNameProps =
  | { text: NonNullable<ButtonProps['text']>; 'aria-label'?: string }
  | { text?: never; 'aria-label': string };

export type MenuButtonProps = MenuButtonBaseProps & MenuButtonNameProps;

/**
 * A small Button that toggles a Popover holding a KDS Menu. Choosing an item
 * closes the menu and runs it. Built on Menu rather than a single-select
 * DropdownList: Menu items hold no selection, so the same item can run again
 * after the menu reopens.
 */
export default function MenuButton({
  items,
  text,
  icon,
  iconPosition,
  kind,
  disabled,
  'aria-label': ariaLabel,
  zIndex,
}: MenuButtonProps) {
  const { triggerProps, popoverProps, closeOnSelect } = useMenuPopover();

  return (
    <>
      <Button
        {...triggerProps}
        size="small"
        kind={kind}
        text={text}
        icon={icon}
        iconPosition={iconPosition}
        disabled={disabled}
        aria-label={ariaLabel}
      />
      <Popover {...popoverProps} zIndex={zIndex}>
        <Menu items={closeOnSelect(items)} />
      </Popover>
    </>
  );
}
